/**
 * New Creative Dialog
 * Format and template selector for creating a new creative
 */

import React, { useMemo, useState } from 'react';
import { X, FileText, ChevronDown } from 'lucide-react';
import { useCreativeStore, useEditorStore, useTemplateStore, useAnimationStore } from '@/stores';
import { getAllTemplates } from '@/templates';
import { createBlankTemplate } from '@/lib/migration/styleToTemplate';
import { PROVIDERS, DEFAULT_FORMAT } from '@/components/providers';
import { getCanvasBackgroundCSS } from '@/types/template';
import { LayerRenderer } from '@/components/canvas/LayerRenderer';
import type { Template } from '@/types/template';
import type { AdFormat } from '@/types/formats';

interface TemplatePreviewCardProps {
  template: Template;
  isSelected: boolean;
  onClick: () => void;
}

function TemplatePreviewCard({ template, isSelected, onClick }: TemplatePreviewCardProps) {
  const previewWidth = 160;
  const previewHeight = (previewWidth * template.canvas.baseHeight) / template.canvas.baseWidth;
  const bgStyle = getCanvasBackgroundCSS(template.canvas.background);
  const scale = previewWidth / template.canvas.baseWidth;

  // Create a format object for the preview
  const previewFormat = useMemo(() => ({
    id: 'preview',
    name: 'Preview',
    width: template.canvas.baseWidth,
    height: template.canvas.baseHeight,
    provider: 'custom' as const,
    platforms: '',
  }), [template.canvas.baseWidth, template.canvas.baseHeight]);

  // Default variable values for preview
  const previewVariables = useMemo(() => {
    const values: Record<string, string> = {};
    template.variables.forEach(v => {
      values[v.id] = v.defaultValue;
    });
    return values;
  }, [template.variables]);

  return (
    <button
      onClick={onClick}
      className={`group p-3 rounded-xl border transition-all text-left ${
        isSelected
          ? 'border-primary bg-primary/10'
          : 'border-white/10 hover:border-primary/50 hover:bg-primary/5'
      }`}
    >
      {/* Live preview */}
      <div
        className="rounded-lg overflow-hidden mb-2 relative"
        style={{
          width: previewWidth,
          height: previewHeight,
          background: bgStyle,
        }}
      >
        <div
          style={{
            width: template.canvas.baseWidth,
            height: template.canvas.baseHeight,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            pointerEvents: 'none',
          }}
        >
          <LayerRenderer
            layers={template.layers}
            format={previewFormat}
            variableValues={previewVariables}
            selectedLayerIds={[]}
            hoveredLayerId={null}
          />
        </div>

        {/* Layer count badge */}
        <div className="absolute bottom-1.5 right-1.5">
          <div className="px-1.5 py-0.5 bg-black/50 rounded text-[9px] text-white">
            {template.layers.length} layers
          </div>
        </div>
      </div>

      <h3 className="text-sm font-medium text-fg mb-0.5">
        {template.metadata.name}
      </h3>
      <p className="text-xs text-fg-muted line-clamp-1">
        {template.metadata.description || 'No description'}
      </p>
    </button>
  );
}

interface NewCreativeDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function NewCreativeDialog({ isOpen, onClose }: NewCreativeDialogProps) {
  const {
    currentCampaignId,
    createCreative,
    updateCreative,
    selectCreative,
  } = useCreativeStore();

  const { content, setFormat } = useEditorStore();
  const { loadTemplate } = useTemplateStore();
  const { clearTimeline } = useAnimationStore();

  const [creativeName, setCreativeName] = useState('');
  const [selectedFormat, setSelectedFormat] = useState<AdFormat>(DEFAULT_FORMAT);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | 'blank' | null>(null);
  const [showFormatPicker, setShowFormatPicker] = useState(false);

  const templates = useMemo(() => getAllTemplates(), []);

  const handleCreate = async () => {
    if (!currentCampaignId || !selectedTemplate) return;

    const isBlank = selectedTemplate === 'blank';
    const template = isBlank ? createBlankTemplate() : selectedTemplate;
    const templateId = isBlank ? 'blank' : template.metadata.id;

    // Generate name if not provided
    const name = creativeName.trim() || `${selectedFormat.name} - ${isBlank ? 'Blank' : template.metadata.name}`;

    // Create the creative with chosen format
    const creative = await createCreative(
      currentCampaignId,
      name,
      selectedFormat.id,
      templateId,
      content
    );

    // Save template data to creative
    await updateCreative(creative.id, {
      templateData: JSON.stringify(template),
      variableValues: {},
    });

    // Load the template and format into the editor
    setFormat(selectedFormat);
    loadTemplate(template);
    clearTimeline();

    // Select the creative and close dialog
    selectCreative(creative.id);
    resetAndClose();
  };

  const handleFormatSelect = (format: AdFormat) => {
    setSelectedFormat(format);
    setShowFormatPicker(false);
  };

  const resetAndClose = () => {
    setCreativeName('');
    setSelectedTemplate(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={resetAndClose}
      />

      {/* Dialog */}
      <div className="relative bg-navy-dark border border-white/10 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-fg">New Creative</h2>
          <button
            onClick={resetAndClose}
            className="p-2 text-fg-muted hover:text-fg hover:bg-white/10 rounded-lg transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Creative Name */}
          <div className="mb-4">
            <label className="block text-xs text-fg-muted mb-2">Name (optional)</label>
            <input
              type="text"
              value={creativeName}
              onChange={(e) => setCreativeName(e.target.value)}
              placeholder="e.g., Summer Sale Hero Banner"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-fg placeholder:text-fg-subtle focus:outline-none focus:border-primary/50"
            />
          </div>

          {/* Format Selector */}
          <div className="mb-6">
            <label className="block text-xs text-fg-muted mb-2">Primary Format</label>
            <div className="relative">
              <button
                onClick={() => setShowFormatPicker(!showFormatPicker)}
                className="w-full flex items-center justify-between px-4 py-3 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors text-left"
              >
                <div>
                  <span className="text-fg font-medium">{selectedFormat.name}</span>
                  <span className="text-fg-muted ml-2 text-sm">
                    {selectedFormat.width} x {selectedFormat.height}
                  </span>
                </div>
                <ChevronDown
                  size={16}
                  className={`text-fg-muted transition-transform ${showFormatPicker ? 'rotate-180' : ''}`}
                />
              </button>

              {/* Format Dropdown */}
              {showFormatPicker && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-navy-light border border-white/10 rounded-lg shadow-xl z-10 max-h-64 overflow-y-auto">
                  {PROVIDERS.map((provider) => (
                    <div key={provider.id}>
                      <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-fg-subtle bg-white/5 sticky top-0">
                        {provider.name}
                      </div>
                      {provider.formats.map((format) => (
                        <button
                          key={format.id}
                          onClick={() => handleFormatSelect(format)}
                          className={`w-full flex items-center justify-between px-4 py-2 text-left hover:bg-white/10 transition-colors ${
                            selectedFormat.id === format.id ? 'bg-primary/10 text-primary' : 'text-fg'
                          }`}
                        >
                          <span className="text-sm">{format.name}</span>
                          <span className="text-xs text-fg-muted">
                            {format.width} x {format.height}
                          </span>
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <p className="text-[10px] text-fg-subtle mt-1">
              You can change formats anytime. Responsive rules adapt your design automatically.
            </p>
          </div>

          {/* Template Selection */}
          <div>
            <label className="block text-xs text-fg-muted mb-3">Start From</label>
            <div className="grid grid-cols-3 gap-3">
              {/* Blank option */}
              <button
                onClick={() => setSelectedTemplate('blank')}
                className={`group p-3 rounded-xl border-2 border-dashed transition-all text-left ${
                  selectedTemplate === 'blank'
                    ? 'border-primary bg-primary/10'
                    : 'border-white/20 hover:border-primary/50 hover:bg-primary/5'
                }`}
              >
                <div
                  className="rounded-lg mb-2 flex items-center justify-center bg-navy-dark"
                  style={{ width: 160, height: 160 }}
                >
                  <FileText size={32} className={`transition-colors ${
                    selectedTemplate === 'blank' ? 'text-primary' : 'text-fg-subtle group-hover:text-primary'
                  }`} />
                </div>
                <h3 className="text-sm font-medium text-fg mb-0.5">Blank</h3>
                <p className="text-xs text-fg-muted">Start from scratch</p>
              </button>

              {/* Templates with live previews */}
              {templates.map((template) => (
                <TemplatePreviewCard
                  key={template.metadata.id}
                  template={template}
                  isSelected={selectedTemplate !== 'blank' && selectedTemplate?.metadata.id === template.metadata.id}
                  onClick={() => setSelectedTemplate(template)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button
            onClick={resetAndClose}
            className="px-4 py-2 text-sm text-fg-muted hover:text-fg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!selectedTemplate}
            className="px-6 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Create Creative
          </button>
        </div>
      </div>
    </div>
  );
}

// Legacy alias
/** @deprecated Use NewCreativeDialog instead */
export const NewProjectDialog = NewCreativeDialog;
